import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.includes("/api/files/") && !url.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    const filePath = url.substring(url.indexOf("/api/files/"));
    return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
  }
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const categories = [
  { value: "all", label: "All Styles" },
  { value: "braids", label: "Braids" },
  { value: "cornrows", label: "Cornrows" },
  { value: "twists", label: "Twists" },
  { value: "locs", label: "Faux Locs" },
];

const GallerySection = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [images, setImages] = useState([]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setImages(res.data);
      } catch (err) {
        // silently fail
      }
    };
    fetchGallery();
  }, []);

  return (
    <section id="gallery" className="py-24 md:py-32" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-sans text-brand-gold mb-4">Our Work</p>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-text tracking-tight">Style Gallery</h2>
          <div className="gold-line-long mx-auto mt-6" />
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex justify-center mb-10">
            <TabsList className="bg-brand-surface border border-brand-border h-auto p-1 gap-1" data-testid="gallery-tabs">
              {categories.map((cat) => (
                <TabsTrigger
                  key={cat.value}
                  value={cat.value}
                  data-testid={`gallery-tab-${cat.value}`}
                  className="font-sans text-sm px-5 py-2 data-[state=active]:bg-brand-gold data-[state=active]:text-black data-[state=active]:shadow-none text-brand-text-muted rounded-none"
                >
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {categories.map((cat) => (
            <TabsContent key={cat.value} value={cat.value}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(cat.value === "all" ? images : images.filter((img) => img.category === cat.value)).map((img, i) => (
                  <motion.div
                    key={`${cat.value}-${img.id || i}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.08, 0.6) }}
                    className="overflow-hidden group rounded-sm border border-brand-border"
                    data-testid={`gallery-image-${i}`}
                  >
                    {img.media_type === "video" ? (
                      <video
                        src={resolveUrl(img.src)}
                        className="w-full gallery-image"
                        controls
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img src={resolveUrl(img.src)} alt={img.alt} loading="lazy" className="w-full gallery-image transition-transform duration-500 group-hover:scale-105" />
                    )}
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Portfolio Link */}
        <div className="text-center mt-12">
          <Link
            to="/portfolio"
            data-testid="view-portfolio-link"
            className="inline-flex items-center gap-2 font-sans text-sm text-brand-gold hover:text-brand-gold-light transition-colors group"
          >
            View Full Portfolio
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
